/**
 * useStartCheckout — mutation-style hook to begin a Stripe Checkout session.
 *
 * `startCheckout(planCode, interval)` POSTs to /api/billing/checkout-session
 * via `billingAgent`, then redirects the browser to the returned `checkout_url`
 * with `window.location.assign` (so the browser back button returns the user to
 * /billing/checkout/cancel rather than the page they left).
 *
 * UX safeguards (design §10 / plan §3):
 *   - `loading` flips true immediately so the CTA can show a spinner + disable.
 *   - `error` surfaces a human message; a 503 maps to "Billing isn't available
 *     yet" per the pinned contract.
 *   - An 8-second timeout fallback sets `error` and clears `loading` if no
 *     redirect URL comes back, so the user is never stranded on a dead click.
 *
 * Mock mode (for Playwright / local E2E without real Stripe):
 *   - `process.env.REACT_APP_USE_MOCK_BILLING === 'true'`, OR
 *   - the page URL has `?stripeMockMode=1`
 *   short-circuits the network call and redirects straight to
 *   `/billing/checkout/success?mock=1`.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { billingAgent } from '../agents/billingAgent';

const CHECKOUT_TIMEOUT_MS = 8000;
const MOCK_SUCCESS_URL = '/billing/checkout/success?mock=1';

/** Whether the app should bypass real Stripe and use the mock redirect. */
export function isMockBillingMode() {
  if (process.env.REACT_APP_USE_MOCK_BILLING === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('stripeMockMode') === '1';
  } catch {
    return false;
  }
}

export function useStartCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const startCheckout = useCallback(async (planCode, interval = 'monthly') => {
    setError(null);
    setLoading(true);

    // Mock mode: skip the network entirely and simulate the Stripe return so
    // Playwright can drive the flow without contacting Stripe.
    if (isMockBillingMode()) {
      window.location.assign(MOCK_SUCCESS_URL);
      return;
    }

    // Arm the 8s fallback so a hung request re-enables the CTA with a message.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setError("Couldn't reach billing — please retry.");
      setLoading(false);
    }, CHECKOUT_TIMEOUT_MS);

    try {
      const data = await billingAgent.startCheckoutSession(planCode, interval);
      if (timerRef.current) clearTimeout(timerRef.current);

      const checkoutUrl = data?.checkout_url;
      if (!checkoutUrl) {
        if (mountedRef.current) {
          setError("Couldn't reach billing — please retry.");
          setLoading(false);
        }
        return;
      }
      // Only navigate away once we have a confirmed URL (never strand the user).
      window.location.assign(checkoutUrl);
    } catch (err) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!mountedRef.current) return;
      const status = err?.status ?? err?.statusCode;
      if (status === 503) {
        setError("Billing isn't available yet.");
      } else {
        setError(err?.message || "Couldn't reach billing — please retry.");
      }
      setLoading(false);
    }
  }, []);

  return { startCheckout, loading, error };
}

export default useStartCheckout;
